import type { QueryClient } from '@tanstack/react-query'
import { createRouter, type RouterHistory } from '@tanstack/react-router'
import type { AuthStore } from '../features/auth/auth-store'
import { loginRoute } from '../features/auth/routes/login'
import { historyRoute } from '../features/inspections/routes/history'
import { inspectionEditorRoute } from '../features/inspections/routes/inspection-editor'
import { myInspectionsRoute } from '../features/inspections/routes/my-inspections'
import { agendaRoute } from '../features/planning/routes/agenda'
import { settingsRoute } from '../features/settings/routes/settings'
import { notificationsRoute } from '../features/sync/routes/notifications'
import { rootRoute, shellRoute, type FieldContext } from './routes/root'

/**
 * Arbre de routes — **routage par code**, pas par fichiers, pour la même raison que le
 * back-office (voir le commentaire de `apps/back-office/src/app/router.ts`) : le mode
 * fichiers suppose un `routeTree.gen.ts` généré que `pnpm typecheck` ne saurait pas
 * produire. Une route par fichier, composant à côté de sa déclaration.
 */
const routeTree = rootRoute.addChildren([
  loginRoute,
  // Les écrans de travail vivent sous la coquille et sa garde de session (D22 pt.1).
  shellRoute.addChildren([
    myInspectionsRoute,
    agendaRoute,
    inspectionEditorRoute,
    notificationsRoute,
    historyRoute,
    settingsRoute,
  ]),
])

/**
 * Fabrique du routeur — alignée sur le back-office : pas d'instance exportée, deux tests
 * ne doivent pas partager l'état de navigation.
 *
 * @param auth - La session : la garde de `shellRoute` la lit avant chaque entrée.
 * @param field - La base locale, la synchro et le jeton : ce dont les écrans ont besoin
 *   pour lire Dexie et pousser la file de propositions.
 * @param history - Injecté par les tests (`createMemoryHistory`) ; en production,
 *   TanStack Router installe de lui-même l'historique du navigateur.
 */
export function createAppRouter(
  queryClient: QueryClient,
  auth: AuthStore,
  field: FieldContext,
  history?: RouterHistory,
) {
  return createRouter({
    routeTree,
    // Rend le `QueryClient` disponible aux `loader` de routes (intégration D19/D21).
    context: { queryClient, auth, ...field },
    // Pas de `defaultPreload: 'intent'` ici : les lectures viendront de Dexie (D21),
    // précharger au survol n'apporte rien sur une tablette au doigt.
    scrollRestoration: true,
    history,
  })
}

/**
 * Enregistrement du routeur auprès de TanStack Router : c'est ce qui rend `<Link to>`,
 * `useNavigate` et les paramètres de route typés **de bout en bout** dans toute l'app.
 */
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}
